<template>
  <div>
    <v-row justify="center" align="center">
      <v-col>
        <!-- UI part to create zip file -->
        <v-card flat class="mx-auto" v-show="!zipFileCreated">
          <ValidationObserver ref="obs" v-slot="{ invalid, validated, passes }">
            <!-- TITLE -->
            <v-card-subtitle
              class="greytext--text"
              v-html="$t('onboarding.deviceSetup.title')"
            ></v-card-subtitle>

            <v-card-text>
              <v-form>
                <!-- SSID -->
                <VTextFieldWithValidation
                  clearable
                  rules="required"
                  v-model="ssid"
                  item-value="value"
                  :label="$t('onboarding.deviceSetup.SSID')"
                  counter
                  :hint="$t('onboarding.deviceSetup.SSIDHint')"
                />

                <!-- PASSWORD -->
                <VTextFieldWithValidation
                  clearable
                  rules="required"
                  v-model="password"
                  counter
                  item-value="value"
                  :label="$t('onboarding.deviceSetup.password')"
                  :append-icon="showpassword ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showpassword ? 'text' : 'password'"
                  :hint="$t('onboarding.deviceSetup.passwordHint')"
                  @click:append="showpassword = !showpassword"
                />

                <!-- PROTOCOL -->
                <VSelectWithValidation
                  clearable
                  rules="required"
                  :items="items"
                  v-model="protocol"
                  item-value="value"
                  :label="$t('onboarding.deviceSetup.protocoll')"
                />

                <!-- COUNTRY CODE -->
                <v-select
                  v-model="selectedCountryCode"
                  :items="getCountryList"
                  item-text="name"
                  item-value="code"
                  :label="$t('onboarding.deviceSetup.countryCode')"
                >
                  <template v-slot:prepend-item>
                    <v-list-item
                      v-for="item in prependCountryList"
                      :key="item.code"
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          <!-- better would be: item[$attrs.item-text] as defined the label property by caller -->
                          {{ item.name }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                </v-select>
              </v-form>
            </v-card-text>

            <!-- SAVE BUTTON -->
            <v-card-actions>
              <v-btn
                @click.prevent="back"
                tile
                elevation="0"
                class="rafiButton petrol white--text pl-2"
                >{{ $t("register.form.backButton") }}</v-btn
              >
              <v-spacer></v-spacer>
              <v-btn
                tile
                elevation="0"
                class="rafiButton petrol white--text"
                @click="passes(save)"
                :disabled="invalid || !validated"
              >
                <v-icon left>mdi-content-save</v-icon
                >{{ $t("onboarding.deviceSetup.save") }}
              </v-btn>
            </v-card-actions>
          </ValidationObserver>
        </v-card>

        <!-- UI part when zip file is created -->
        <v-card flat v-show="zipFileCreated">
          <v-card-title
            class="petrol--text font-weight-bold justify-center"
            v-html="$t('onboarding.deviceSetup.finished.title')"
          ></v-card-title>

          <v-card-text
            class="greytext--text"
            v-html="$t('onboarding.deviceSetup.finished.zip')"
          ></v-card-text>
          <v-card-text
            class="greytext--text"
            v-html="$t('onboarding.deviceSetup.finished.hint')"
          ></v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              elevation="0"
              tile
              class="rafiButton petrol white--text"
              @click="NextStep"
            >
              {{ $t("register.form.confirm.continue") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar" :color="snackbarColor">
      {{ snackbarText }}
      <v-btn color="white" text @click="snackbar = false">{{
        $t("onboarding.deviceSetup.close")
      }}</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import VTextFieldWithValidation from "../../inputs/VTextFieldWithValidation";
import VSelectWithValidation from "../../inputs/VSelectWithValidation";
import { mapGetters, mapActions } from "vuex";
import fileService from "@/services/fileService";

export default {
  name: "device-setup",
  components: {
    ValidationObserver,
    VTextFieldWithValidation,
    VSelectWithValidation,
  },
  computed: {
    ...mapGetters("onboarding", [
      "getToken",
      "getSSID",
      "getPassword",
      "getProtocoll",
      "getCountryCode",
      "getCountryList",
    ]),
    ssid: {
      get() {
        return this.getSSID;
      },
      set(value) {
        this.setSSID(value);
      },
    },
    password: {
      get() {
        return this.getPassword;
      },
      set(value) {
        this.setPassword(value);
      },
    },
    protocol: {
      get() {
        return this.getProtocoll;
      },
      set(value) {
        this.setProtocoll(value);
      },
    },
    selectedCountryCode: {
      get() {
        return this.getCountryCode;
      },
      set(value) {
        this.setCountryCode(value);
      },
    },
  },
  data: () => ({
    zipFileCreated: false,
    snackbarColor: "success",
    snackbar: false,
    snackbarText: "",
    valid: true,
    showpassword: false,
    prependCountryList: [{ name: "Germany", code: "DE" }],
    rules: {
      required: (value) => !!value || this.$t("onboarding.data.required"),
      min8: (v) => v.length >= 8 || this.$t("onboarding.data.min8"),
      max32: (v) => v.length <= 32 || this.$t("onboarding.data.less32"),
    },
    ssidRules: [
      (v) => !!v || this.$t("onboarding.data.nameRequired"),
      (v) => (v && v.length <= 32) || this.$t("onboarding.data.nameLess32"),
    ],
    items: [
      {
        text: "WPA-PSK (CCMP)",
        value: "psk",
      },
      {
        text: "WPA2-PSK (CCMP)",
        value: "psk2",
      },
    ],
  }),

  mounted() {
    this.zipFileCreated = false;
    this.ssid = "";
    this.password = "";
    this.$refs.obs.reset();
  },
  methods: {
    ...mapActions("onboarding", [
      "setSSID",
      "setPassword",
      "setProtocoll",
      "resetLevel",
      "setCountryCode",
    ]),
    NextStep() {
      window.open("https://kismanager.kisme.com", "_blank");
    },
    back() {
      this.ssid = null;
      this.password = null;
      this.protocol = null;
      this.selectedCountryCode = "DE";

      //back in slider
      this.resetLevel();
    },
    async save() {
      //create downloadable blob with encrypted content
      let blob = await fileService.createEncryptedBlob(
        this.ssid,
        this.password,
        this.protocol,
        this.selectedCountryCode
      );

      if (blob == null) {
        this.snackbarText = this.$t("onboarding.data.cannotEncryptBlob");
        this.snackbarColor = "error";
        this.snackbar = true;
        this.zipFileCreated = false;
        return;
      }

      //encrypt token
      let token = await fileService.createEncryptedToken(this.getToken);
      if (token == null) {
        this.snackbarText = this.$t("onboarding.data.cannotEncryptToken");
        this.snackbarColor = "error";
        this.snackbar = true;
        this.zipFileCreated = false;
        return;
      }

      //create zip file and start download
      let result = await fileService.zipAndDownload(blob, token);

      if (!result) {
        this.snackbarText = this.$t("onboarding.data.cannotCreateFile");
        this.snackbarColor = "error";
        this.snackbar = true;
        this.zipFileCreated = false;
      } else {
        this.zipFileCreated = true;
      }
    },
  },
};
</script>
